import { useState } from "react"
import { Camera } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { useToast } from "@/hooks/use-toast"

interface ProfileAvatarProps {
  avatarUrl?: string | null
  firstName?: string | null
  lastName?: string | null
  onAvatarUpdate: () => void
  isEditing: boolean
}

export function ProfileAvatar({ 
  avatarUrl, 
  firstName, 
  lastName, 
  onAvatarUpdate,
  isEditing 
}: ProfileAvatarProps) {
  const [uploading, setUploading] = useState(false)
  const { toast } = useToast()

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error("User not found")

      if (!event.target.files || event.target.files.length === 0) {
        throw new Error("Você precisa selecionar uma imagem para fazer upload.")
      }

      const file = event.target.files[0]
      const fileExt = file.name.split(".").pop()
      const fileName = `${user.id}-${Date.now()}.${fileExt}`

      // Upload do arquivo para o bucket 'avatars'
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, { 
          upsert: true,
          contentType: file.type 
        })

      if (uploadError) throw uploadError

      // Obter a URL pública do arquivo
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName)

      // Atualizar o perfil com a nova URL do avatar
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ 
          avatar_url: publicUrl,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id)

      if (updateError) throw updateError

      toast({
        title: "Avatar atualizado com sucesso!",
        description: "Sua foto de perfil foi atualizada.",
        variant: "default",
      })
      
      onAvatarUpdate()
    } catch (error) {
      console.error("Error uploading avatar:", error)
      toast({
        variant: "destructive",
        title: "Erro ao fazer upload do avatar",
        description: "Ocorreu um erro ao atualizar sua foto. Por favor tente novamente.",
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="relative">
      <Avatar className="h-32 w-32">
        <AvatarImage src={avatarUrl || "/placeholder.svg"} />
        <AvatarFallback>
          {firstName?.[0]}
          {lastName?.[0]}
        </AvatarFallback>
      </Avatar>
      {isEditing && (
        <label
          htmlFor="avatar"
          className="absolute bottom-0 right-0 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Camera className="h-4 w-4" />
          <input
            type="file"
            id="avatar"
            accept="image/*"
            className="hidden"
            onChange={uploadAvatar}
            disabled={uploading}
          />
        </label>
      )}
    </div>
  )
}